const FLIP_DURATION = 750;

/**
 * Mobile menu with Mens / Womens tabs. Each tab shows collection-group tiles; tapping a tile flips
 * the panel to that group's collections. Opening, closing and focus trapping stay with <header-drawer>.
 */
class LuxeMenu extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;
  /** @type {HTMLElement | null} */
  #lastTile = null;

  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('.luxe-menu__tab'));
    this.panels = Array.from(this.querySelectorAll('.luxe-menu__panel'));
    this.scroller = this.querySelector('.luxe-menu__scroll');
    this.activeIndex = Number(this.dataset.defaultTab) || 0;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.selectTab(Number(tab.dataset.tab)), { signal });
      tab.addEventListener('keydown', this.#onTabKeydown, { signal });
    });
    this.addEventListener('click', this.#onClick, { signal });

    // Return to the first screen whenever the drawer finishes closing
    this.closest('details')?.addEventListener(
      'toggle',
      (event) => {
        if (event.target instanceof HTMLDetailsElement && !event.target.open) this.#reset();
      },
      { signal }
    );

    this.style.setProperty('--luxe-active-tab', String(this.activeIndex));
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @param {number} index */
  selectTab(index, { focus = false } = {}) {
    if (index === this.activeIndex || !this.panels[index]) {
      if (focus) this.tabs[index]?.focus();
      return;
    }

    const direction = index > this.activeIndex ? 'forward' : 'back';
    const previous = this.panels[this.activeIndex];
    const next = this.panels[index];

    this.#unflip(previous, { animate: false });
    previous.classList.remove('is-active', 'is-entering');
    previous.hidden = true;

    next.hidden = false;
    next.dataset.direction = direction;
    next.classList.remove('is-entering');
    // Restart the entrance animations
    void next.offsetWidth;
    next.classList.add('is-active', 'is-entering');

    this.tabs.forEach((tab, i) => tab.setAttribute('aria-selected', String(i === index)));
    this.style.setProperty('--luxe-active-tab', String(index));
    this.activeIndex = index;
    this.scroller?.scrollTo({ top: 0, behavior: 'smooth' });
    if (focus) this.tabs[index]?.focus();
  }

  #onTabKeydown = (event) => {
    const keys = { ArrowRight: 1, ArrowLeft: -1 };
    if (!(event.key in keys)) return;
    event.preventDefault();
    const count = this.tabs.length;
    this.selectTab((this.activeIndex + keys[event.key] + count) % count, { focus: true });
  };

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const tile = event.target.closest('button.luxe-menu__tile[data-group]');
    if (tile instanceof HTMLElement) {
      this.#flipTo(tile);
      return;
    }

    if (event.target.closest('.luxe-menu__back')) {
      const panel = event.target.closest('.luxe-menu__panel');
      if (panel instanceof HTMLElement) this.#unflip(panel);
    }
  };

  /** @param {HTMLElement} tile */
  #flipTo(tile) {
    const panel = tile.closest('.luxe-menu__panel');
    const group = tile.dataset.group ? this.querySelector(`#${CSS.escape(tile.dataset.group)}`) : null;
    if (!(panel instanceof HTMLElement) || !(group instanceof HTMLElement)) return;

    panel.querySelectorAll('.luxe-menu__group').forEach((other) => {
      if (other instanceof HTMLElement) other.hidden = other !== group;
    });

    this.#lastTile = tile;
    panel.querySelector('.luxe-menu__face--front')?.setAttribute('inert', '');
    panel.querySelector('.luxe-menu__face--back')?.removeAttribute('inert');
    panel.classList.add('is-flipped');
    this.scroller?.scrollTo({ top: 0, behavior: 'smooth' });

    setTimeout(() => group.querySelector('.luxe-menu__back')?.focus({ preventScroll: true }), FLIP_DURATION / 2);
  }

  /** @param {HTMLElement} panel */
  #unflip(panel, { animate = true } = {}) {
    if (!panel.classList.contains('is-flipped')) return;

    const card = panel.querySelector('.luxe-menu__card');
    if (!animate && card instanceof HTMLElement) card.style.transition = 'none';

    panel.classList.remove('is-flipped');
    panel.querySelector('.luxe-menu__face--back')?.setAttribute('inert', '');
    panel.querySelector('.luxe-menu__face--front')?.removeAttribute('inert');

    if (!animate && card instanceof HTMLElement) {
      void card.offsetWidth;
      card.style.transition = '';
    }

    if (animate && this.#lastTile && panel.contains(this.#lastTile)) {
      const tile = this.#lastTile;
      setTimeout(() => tile.focus({ preventScroll: true }), FLIP_DURATION / 2);
    }
  }

  #reset() {
    this.panels.forEach((panel) => this.#unflip(panel, { animate: false }));
    const defaultTab = Number(this.dataset.defaultTab) || 0;
    if (defaultTab !== this.activeIndex) this.selectTab(defaultTab);
    this.panels[this.activeIndex]?.classList.remove('is-entering');
    if (this.scroller) this.scroller.scrollTop = 0;
  }
}

if (!customElements.get('luxe-menu')) {
  customElements.define('luxe-menu', LuxeMenu);
}
